import React, { useState } from "react";
import { Check, ArrowRight, FileText, Link2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

export const ServiceDetail = ({ service, onClose, onInquire }) => {
  const open = Boolean(service);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    const url = `${window.location.origin}/services/${service.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy. Link: " + url);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="service-detail-modal"
        className="max-w-2xl p-0 gap-0 overflow-hidden rounded-[2px] border border-[rgba(4,22,46,0.14)] bg-[#F4F6F3] text-[#04162E]"
      >
        {service && (
          <>
            <div className="relative p-6 bg-[#04162E] text-white">
              <div className="absolute inset-0 blueprint-grid opacity-70" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3 pr-8">
                  <span className="font-display text-[12.5px] font-bold border border-white/50 px-2 py-[3px] leading-[1.4]">
                    {service.code}
                  </span>
                  <span className="font-body text-xs text-[#8FA0B3]">{service.standard}</span>
                  <button
                    type="button"
                    onClick={copyLink}
                    data-testid="service-copy-link"
                    className="ml-auto inline-flex items-center gap-1.5 font-body text-xs text-[#D7DEE7] hover:text-white border border-white/30 hover:border-white px-2.5 py-1 transition-colors"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    {copied ? "Copied" : "Copy link"}
                  </button>
                </div>
                <DialogHeader className="space-y-2 text-left">
                  <DialogTitle className="font-display text-2xl sm:text-[28px] font-bold text-white text-left leading-tight">
                    {service.title}
                  </DialogTitle>
                  <DialogDescription className="font-body text-[#C7D1DC] text-sm leading-relaxed text-left">
                    {service.short}
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>

            <ScrollArea className="max-h-[52vh]">
              <div className="p-6">
                <p className="font-body text-[14.5px] leading-[1.65] text-[#5C6C80]">{service.long}</p>

                <h4 className="mt-6 mb-3 font-body text-xs font-semibold tracking-[0.04em] text-[#0B6B44] uppercase">
                  Services Offered
                </h4>
                <ul className="border-t border-[rgba(4,22,46,0.14)]">
                  {service.items.map((it, i) => (
                    <li
                      key={it.t}
                      className="flex gap-3 py-3.5 border-b border-[rgba(4,22,46,0.14)]"
                      data-testid={`service-item-${service.id}-${i}`}
                    >
                      <Check className="h-4 w-4 shrink-0 text-[#0E8A57] mt-1" />
                      <div>
                        <div className="font-semibold text-[#04162E] text-[15px]">{it.t}</div>
                        <div className="font-body text-sm text-[#5C6C80] leading-[1.6] mt-0.5">{it.d}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                {service.note && (
                  <div className="mt-5 flex gap-3 border border-[rgba(4,22,46,0.14)] bg-white p-4">
                    <FileText className="h-4 w-4 shrink-0 text-[#0B6B44] mt-0.5" />
                    <p className="font-body text-xs leading-relaxed text-[#5C6C80]">{service.note}</p>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="p-5 border-t border-[rgba(4,22,46,0.14)] bg-white">
              <button
                data-testid="service-inquire-button"
                onClick={() => onInquire(service)}
                className="group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[14.5px] font-semibold text-white bg-[#04162E] hover:bg-[#12335A] rounded-[2px] transition-colors"
              >
                Inquire About This Service
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
